/**
 * KeyboardShortcutsHelp Component
 *
 * Modal dialog showing all available keyboard shortcuts
 * Features:
 * - Grouped by category/feature
 * - Context-aware (shows only relevant shortcuts)
 * - Platform-specific display (Mac vs Windows/Linux)
 * - Triggered by pressing "?" key
 *
 * @example
 * ```tsx
 * <KeyboardShortcutsHelp />
 * ```
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, Keyboard } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FocusTrap } from '@/components/ui/focus-trap';
import { VisuallyHidden } from '@/components/ui/visually-hidden';
import {
  getAllShortcuts,
  formatShortcut,
  type KeyboardShortcut,
} from '@/hooks/useKeyboardShortcuts';

export interface KeyboardShortcutsHelpProps {
  /** Whether to show the dialog initially */
  defaultOpen?: boolean;
  /** Callback when dialog opens/closes */
  onOpenChange?: (open: boolean) => void;
  /** Additional className */
  className?: string;
}

export const KeyboardShortcutsHelp: React.FC<KeyboardShortcutsHelpProps> = ({
  defaultOpen = false,
  onOpenChange,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  }, [onOpenChange]);

  // Listen for "?" key to open the dialog
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if "?" is pressed (Shift + /)
      if (event.key === '?' && !isOpen) {
        // Don't trigger if typing in an input
        const target = event.target as HTMLElement;
        const isInput =
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.contentEditable === 'true';

        if (!isInput) {
          event.preventDefault();
          handleOpenChange(true);
        }
      }

      // Close on Escape
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        handleOpenChange(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleOpenChange]);

  // Prevent body scroll when dialog is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const groupedShortcuts = getAllShortcuts();

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9998] bg-background/80 backdrop-blur-sm"
        onClick={() => handleOpenChange(false)}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="shortcuts-title"
        className={cn(
          'fixed left-1/2 top-1/2 z-[9999]',
          'w-full max-w-3xl max-h-[85vh]',
          '-translate-x-1/2 -translate-y-1/2',
          'bg-background border border-border rounded-lg shadow-lg',
          'flex flex-col',
          className
        )}
      >
        <FocusTrap enabled={isOpen}>
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-md">
                <Keyboard className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2
                  id="shortcuts-title"
                  className="text-xl font-semibold text-foreground"
                >
                  Keyboard Shortcuts
                </h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Press{' '}
                  <kbd className="px-1.5 py-0.5 text-xs font-mono bg-muted border border-border rounded">
                    ?
                  </kbd>{' '}
                  to toggle this dialog
                </p>
              </div>
            </div>

            <button
              onClick={() => handleOpenChange(false)}
              className={cn(
                'p-2 rounded-md',
                'hover:bg-muted transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
              )}
              aria-label="Close keyboard shortcuts help"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-8">
              {Object.entries(groupedShortcuts).map(([group, shortcuts]) => {
                if (shortcuts.length === 0) return null;

                return (
                  <section key={group}>
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                      {group}
                    </h3>
                    <div className="space-y-2">
                      {shortcuts.map((shortcut, index) => (
                        <ShortcutRow key={shortcut.id || index} shortcut={shortcut} />
                      ))}
                    </div>
                  </section>
                );
              })}

              {Object.values(groupedShortcuts).every((shortcuts) => shortcuts.length === 0) && (
                <div className="text-center py-12">
                  <Keyboard className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <p className="text-muted-foreground">
                    No keyboard shortcuts registered yet.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-border bg-muted/30">
            <p className="text-xs text-muted-foreground text-center">
              Keyboard shortcuts help you navigate LegalSyncUp more efficiently.
              Press{' '}
              <kbd className="px-1.5 py-0.5 text-xs font-mono bg-background border border-border rounded">
                Esc
              </kbd>{' '}
              to close this dialog.
            </p>
          </div>

          {/* Screen reader announcement */}
          <VisuallyHidden>
            <div role="status" aria-live="polite">
              {isOpen
                ? 'Keyboard shortcuts help dialog opened'
                : 'Keyboard shortcuts help dialog closed'}
            </div>
          </VisuallyHidden>
        </FocusTrap>
      </div>
    </>
  );
};

/**
 * Individual shortcut row component
 */
interface ShortcutRowProps {
  shortcut: KeyboardShortcut;
}

const ShortcutRow: React.FC<ShortcutRowProps> = ({ shortcut }) => {
  return (
    <div className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/50 transition-colors">
      <span className="text-sm text-foreground">{shortcut.description}</span>
      <div className="flex items-center gap-1">
        {formatShortcut(shortcut)
          .split('+')
          .map((key, index, array) => (
            <React.Fragment key={index}>
              <kbd
                className={cn(
                  'px-2 py-1 text-xs font-mono',
                  'bg-background border border-border rounded',
                  'text-foreground shadow-sm',
                  'min-w-[2rem] text-center'
                )}
              >
                {key}
              </kbd>
              {index < array.length - 1 && (
                <span className="text-muted-foreground text-xs">+</span>
              )}
            </React.Fragment>
          ))}
      </div>
    </div>
  );
};

export default KeyboardShortcutsHelp;
